'use client';

import { useTrackingContexts } from '@hc-frontend/core-ui';
import type { SelfDescribingJson } from '@snowplow/browser-tracker';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useRef } from 'react';

import { useSmidContext } from '../../hooks/use-smid-context';
import { useTrackPageView } from '../../hooks/use-track-page-view';
import type { TrackPageViewProps } from './types';

function equalsReflexiveContext(
  context: SelfDescribingJson[],
  parentContext: SelfDescribingJson[],
) {
  const reflexiveParentContext = parentContext.find((ctx) =>
    ctx.schema.includes('reflexive_funnel'),
  );

  if (!reflexiveParentContext) {
    return true;
  }

  return (
    context.find((ctx) => ctx.schema.includes('reflexive_funnel'))?.data
      .plate_id === reflexiveParentContext.data.plate_id
  );
}

export function TrackPageViewClient({
  loadAction,
  forcePageView,
}: TrackPageViewProps) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const trackingContexts = useTrackingContexts();
  const trackPageView = useTrackPageView();
  const smidContext = useSmidContext();

  // Every SMID a consumer is exposed to (URL param, page footer) rides
  // alongside whatever contexts the page already tracks — not just the
  // reflexive-funnel context compared below.
  const contexts = useMemo(
    () => (smidContext ? [...trackingContexts, smidContext] : trackingContexts),
    [trackingContexts, smidContext],
  );

  // `smidContext` resolves asynchronously — useSmidContext's page-footer DOM
  // read lands via its own useLayoutEffect one render after mount/navigation,
  // and that state update re-runs the effect below a second time with
  // fresher data. Suppressing that re-run (e.g. via a ref) would ship the
  // stale, pre-resolution context instead of just deduping the call, so
  // instead every fire is deferred a microtask and tagged with a token:
  // only the LAST fire of any same-navigation burst actually calls
  // trackPageView, and it does so with whatever context was current at that
  // point — see MP-3857.
  const fireTokenRef = useRef(0);

  useEffect(() => {
    const token = ++fireTokenRef.current;

    const firePageView = (context: SelfDescribingJson[]) => {
      queueMicrotask(() => {
        if (fireTokenRef.current === token) {
          trackPageView({ context });
        }
      });
    };

    const loadContext = async () => {
      const isPII = pathname.includes('personal-information');

      if (!loadAction) {
        if (!isPII || forcePageView) {
          firePageView(contexts);
        }
        return;
      }

      const context = await loadAction();
      if (context && (!isPII || forcePageView)) {
        if (equalsReflexiveContext(context, trackingContexts)) {
          firePageView(contexts);
        } else {
          router.refresh();
        }
      }
    };

    loadContext();
  }, [
    pathname,
    loadAction,
    searchParams,
    router,
    trackPageView,
    trackingContexts,
    contexts,
    forcePageView,
  ]);

  return null;
}
